class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector('details');
    this.content = this.mainDetailsToggle.querySelector('summary').nextElementSibling;

    this.mainDetailsToggle.addEventListener('focusout', this.onFocusOut.bind(this));
    this.mainDetailsToggle.addEventListener('toggle', this.onToggle.bind(this));
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onToggle() {
    if (!this.animations) this.animations = this.content.getAnimations();

    if (this.mainDetailsToggle.hasAttribute('open')) {
      this.animations.forEach((animation) => animation.play());
    } else {
      this.animations.forEach((animation) => animation.cancel());
    }
  }

  close() {
    this.mainDetailsToggle.removeAttribute('open');
    this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', false);
  }
}

customElements.define('details-disclosure', DetailsDisclosure);

class HeaderMenu extends DetailsDisclosure {
  constructor() {
    super();
    this.header = document.querySelector('.header-wrapper');
  }

  onToggle() {
    if (!this.header) return;
    this.header.preventHide = this.mainDetailsToggle.open;

    if (document.documentElement.style.getPropertyValue('--header-bottom-position-desktop') !== '') return;
    document.documentElement.style.setProperty(
      '--header-bottom-position-desktop',
      `${Math.floor(this.header.getBoundingClientRect().bottom)}px`
    );
  }
}

customElements.define('header-menu', HeaderMenu);

/* ===========================================================================
   FRAMESI · <fx-nav-menu> — un solo desplegable para dos presentaciones
   Frente F-B (CABECERA). Lo usan snippets/header-mega-menu.liquid (panel de
   escritorio) y snippets/header-drawer.liquid (acordeón dentro del cajón que ya
   existía). La diferencia entre los dos es CSS; el comportamiento es el mismo.

   POR QUÉ NO ES UN <details>: hace falta `aria-expanded` sincronizado en el
   disparador y control de cuándo se abre al pasar el ratón. Con <details> el
   estado lo lleva el navegador y el hover habría que fingirlo con CSS, que no
   puede tocar aria-expanded.

   CONDICIONES QUE ESTE ARCHIVO TIENE QUE CUMPLIR:
   · el clic y el toque abren SIEMPRE;
   · el ratón sólo abre donde hay hover de verdad y puntero fino — se pregunta
     por `(hover:hover) and (pointer:fine)`, nunca por ancho de pantalla, para
     que un portátil táctil o un iPad no se queden con un menú que se abre solo
     al rozarlo;
   · Escape cierra el nivel abierto y devuelve el foco al disparador; se corta
     ahí la propagación para que el cajón NO se cierre al mismo tiempo. El
     siguiente Escape ya no encuentra nada abierto, llega a assets/framesi.js y
     cierra el cajón: es el orden que espera quien navega con teclado;
   · nada de `data-panel`: ese atributo es el contrato del cajón y de la hoja de
     búsqueda con assets/framesi.js, que sincroniza aria-hidden y MUEVE EL FOCO
     al abrir. Un panel que se abre al pasar el ratón no puede robar el foco.
   ======================================================================== */
(function () {
  if (window.customElements.get('fx-nav-menu')) return;

  var punteroFino = window.matchMedia('(hover:hover) and (pointer:fine)');
  var abiertos = [];

  function registrar(el) {
    if (abiertos.indexOf(el) === -1) abiertos.push(el);
  }

  function olvidar(el) {
    var i = abiertos.indexOf(el);
    if (i !== -1) abiertos.splice(i, 1);
  }

  class FxNavMenu extends HTMLElement {
    connectedCallback() {
      if (this.__fxListo) return;
      this.__fxListo = true;

      this.disparador = this.propio('[data-fx-toggle]');
      this.panel = this.propio('[data-fx-panel]');
      if (!this.disparador || !this.panel) return;

      this.abierto = false;
      this.temporizador = null;
      this.flotante = this.hasAttribute('data-fx-hover');

      this.disparador.addEventListener('click', this.alPulsar.bind(this));
      this.addEventListener('keydown', this.alTeclear.bind(this));

      if (this.flotante) {
        // Si el foco sale del grupo, el panel flotante sobra. En el acordeón del
        // cajón no: allí seguir abierto es lo que espera quien lo desplegó.
        this.addEventListener('focusout', this.alSalirElFoco.bind(this));
        this.addEventListener('mouseenter', this.alEntrarElRaton.bind(this));
        this.addEventListener('mouseleave', this.alSalirElRaton.bind(this));
      }

      this.cerrar(false);
    }

    /* El primer descendiente que cumple el selector Y cuyo dueño soy yo: así un
       acordeón anidado no se roba el disparador del de fuera. */
    propio(selector) {
      var todos = this.querySelectorAll(selector);
      for (var i = 0; i < todos.length; i++) {
        if (todos[i].closest('fx-nav-menu') === this) return todos[i];
      }
      return null;
    }

    abrir(porRaton) {
      if (this.abierto) return;
      this.abierto = true;
      // Abierto al pasar el ratón ≠ abierto a propósito. La diferencia decide
      // dos cosas: si se cierra solo al salir el ratón, y qué hace el clic.
      this.porRaton = porRaton === true;
      this.classList.add('is-open');
      this.disparador.setAttribute('aria-expanded', 'true');
      registrar(this);
    }

    cerrar(devolverFoco) {
      window.clearTimeout(this.temporizador);
      this.porRaton = false;
      if (this.abierto || this.disparador.getAttribute('aria-expanded') !== 'false') {
        this.abierto = false;
        this.classList.remove('is-open');
        this.disparador.setAttribute('aria-expanded', 'false');
      }
      olvidar(this);
      if (devolverFoco) this.disparador.focus({ preventScroll: true });
    }

    alPulsar(e) {
      e.preventDefault();
      if (!this.abierto) {
        this.abrir(false);
        return;
      }
      // Con ratón, el panel YA está abierto cuando llega el clic: el puntero
      // entró antes que el botón. Cerrarlo aquí haría que «pulsar» pareciera
      // «apagar». El primer clic lo fija —deja de depender del ratón— y el
      // segundo sí lo cierra.
      if (this.porRaton) {
        window.clearTimeout(this.temporizador);
        this.porRaton = false;
        return;
      }
      this.cerrar(false);
    }

    alTeclear(e) {
      if (e.key !== 'Escape') return;
      if (!this.abierto) return;
      // Corta aquí: el cajón (framesi.js) sólo debe cerrarse con el SIGUIENTE
      // Escape, cuando ya no quede ningún nivel desplegado.
      e.stopPropagation();
      this.cerrar(true);
    }

    alSalirElFoco() {
      window.setTimeout(
        function () {
          if (this.abierto && !this.contains(document.activeElement)) this.cerrar(false);
        }.bind(this)
      );
    }

    alEntrarElRaton() {
      if (!punteroFino.matches) return;
      window.clearTimeout(this.temporizador);
      // Nunca mueve el foco: abrir al pasar el ratón no es una acción del
      // usuario sobre el teclado.
      this.abrir(true);
    }

    alSalirElRaton() {
      if (!punteroFino.matches) return;
      // Fijado con un clic o abierto con el teclado: el ratón ya no manda.
      if (!this.porRaton) return;
      window.clearTimeout(this.temporizador);
      this.temporizador = window.setTimeout(
        function () {
          // Si mientras tanto alguien entró con el teclado, el ratón no cierra.
          if (this.contains(document.activeElement)) return;
          this.cerrar(false);
        }.bind(this),
        160
      );
    }
  }

  customElements.define('fx-nav-menu', FxNavMenu);

  /* Un clic fuera cierra los paneles flotantes. El acordeón del cajón no se
     toca: vive dentro de un diálogo que ya tiene su propio cierre. */
  document.addEventListener(
    'pointerdown',
    function (e) {
      if (!abiertos.length) return;
      abiertos.slice().forEach(function (el) {
        if (!el.flotante) return;
        if (el.contains(e.target)) return;
        el.cerrar(false);
      });
    },
    true
  );

  /* Escape con el foco fuera del grupo (caso típico: se abrió al pasar el
     ratón). No corta la propagación: framesi.js sigue recibiendo la tecla. */
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    if (!abiertos.length) return;
    abiertos.slice().forEach(function (el) {
      if (!el.flotante) return;
      if (el.contains(document.activeElement)) return;
      el.cerrar(false);
    });
  });

  /* Si el puntero deja de ser fino (se conecta una pantalla táctil, gira una
     tableta), se cierra lo que hubiera abierto por hover. */
  var alCambiarElPuntero = function () {
    if (punteroFino.matches) return;
    abiertos.slice().forEach(function (el) {
      if (el.flotante) el.cerrar(false);
    });
  };

  if (typeof punteroFino.addEventListener === 'function') {
    punteroFino.addEventListener('change', alCambiarElPuntero);
  } else if (typeof punteroFino.addListener === 'function') {
    punteroFino.addListener(alCambiarElPuntero);
  }
})();
